import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { Settings } from 'lucide-react';
import axios from 'axios';
import './App.css';

// Import pages
import Login from './pages/Login';
import Register from './pages/Register';
import Marketplace from './pages/Marketplace';
import ProducerDashboard from './pages/ProducerDashboard';
import AdminPanel from './pages/AdminPanel';
import ProductDetails from './pages/ProductDetails';
import UserProfile from './pages/UserProfile';
import ForgotPassword from './pages/ForgotPassword';
import TermosDeUso from './pages/TermosDeUso';
import PoliticaPrivacidade from './pages/PoliticaPrivacidade';
import QuemSomos from './pages/QuemSomos';
import PoliticaCookies from './pages/PoliticaCookies';
import CentralAjuda from './pages/CentralAjuda';

// Import components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// API setup
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = "/api";

// Auth context
const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState(null);

  // WebSocket for online users tracking
  useEffect(() => {
    // Create a stable anonymous id per device
    const getAnonId = () => {
      const key = 'anon_id';
      let id = localStorage.getItem(key);
      if (!id) {
        id = `anon-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        localStorage.setItem(key, id);
      }
      return id;
    };

    const wsUserId = user?.id || getAnonId();
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${proto}://${window.location.host}/ws/${wsUserId}`;

    let ws;
    let ping;

    try {
      ws = new WebSocket(wsUrl);
      ping = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 25000);
    } catch (e) {
      // ignore
    }

    return () => {
      if (ping) clearInterval(ping);
      if (ws) {
        try { ws.close(); } catch (e) {}
      }
    };
  }, [user]);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Load site settings first
        const settingsResponse = await fetch(`${API}/settings/public`);
        if (settingsResponse.ok) {
          const settings = await settingsResponse.json();
          setSiteSettings(settings);
          
          // Update document title and meta description
          document.title = settings.site_title || 'Nossa Feira - Marketplace Local';
          
          // Update meta description
          let metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription) {
            metaDescription.setAttribute('content', settings.meta_description || 'Marketplace que conecta produtores rurais locais diretamente aos consumidores');
          }
          
          // Update CSS custom properties for colors
          if (settings.site_color_primary) {
            document.documentElement.style.setProperty('--feira-green', settings.site_color_primary);
          }
          if (settings.site_color_secondary) {
            document.documentElement.style.setProperty('--feira-orange', settings.site_color_secondary);
          }
        }
      } catch (error) {
        console.error('Failed to load site settings:', error);
      }

      // Check for stored token on app start
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        setUser(JSON.parse(userData));
        // Set default axios header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      setLoading(false);
    };

    initializeApp();
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-orange-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500"></div>
      </div>
    );
  }

  // Create a maintenance page component
  const MaintenancePage = () => (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 flex items-center justify-center">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Settings className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {siteSettings?.site_name || 'Nossa Feira'}
        </h1>
        <h2 className="text-xl font-semibold text-orange-600 mb-4">
          Site em Manutenção
        </h2>
        <p className="text-gray-600 mb-6">
          Estamos realizando melhorias no sistema. Voltaremos em breve com novidades!
        </p>
        <div className="text-sm text-gray-500">
          <p>Agradecemos a compreensão.</p>
          <p className="mt-2">
            Contato: {siteSettings?.contact_email || 'contato@nossafeira.com'}
          </p>
        </div>
        
        {/* Admin access link - discrete */}
        <div className="mt-8 pt-4 border-t border-gray-200">
          <Link 
            to="/login" 
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Acesso Administrativo
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <AuthContext.Provider value={{ user, login, logout, API, siteSettings }}>
      <div className="App min-h-screen bg-gradient-to-br from-green-50 to-orange-50">
        <BrowserRouter>
          <Navbar />
          <Routes>
            {/* Always allow login and admin access */}
            <Route path="/login" element={!user ? <Login /> : <Navigate to={user.user_type === 'produtor' ? '/produtor' : user.role === 'admin' ? '/admin' : '/marketplace'} />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/profile" element={user ? <UserProfile /> : <Navigate to="/login" />} />
            
            {/* Check maintenance mode for other routes */}
            <Route path="/" element={
              siteSettings?.maintenance_mode && (!user || user.role !== 'admin') 
                ? <MaintenancePage /> 
                : (user ? <Navigate to={user.user_type === 'produtor' ? '/produtor' : '/marketplace'} /> : <Navigate to="/marketplace" />)
            } />
            <Route path="/register" element={
              siteSettings?.maintenance_mode && (!user || user.role !== 'admin') 
                ? <MaintenancePage /> 
                : (!user ? <Register /> : <Navigate to={user.user_type === 'produtor' ? '/produtor' : '/marketplace'} />)
            } />
            <Route path="/forgot-password" element={
              siteSettings?.maintenance_mode && (!user || user.role !== 'admin') 
                ? <MaintenancePage /> 
                : (!user ? <ForgotPassword /> : <Navigate to={user.user_type === 'produtor' ? '/produtor' : '/marketplace'} />)
            } />
            <Route path="/marketplace" element={
              siteSettings?.maintenance_mode && (!user || user.role !== 'admin') 
                ? <MaintenancePage /> 
                : <Marketplace />
            } />
            <Route path="/product/:productId" element={
              siteSettings?.maintenance_mode && (!user || user.role !== 'admin') 
                ? <MaintenancePage /> 
                : <ProductDetails />
            } />
            <Route path="/produtor" element={
              siteSettings?.maintenance_mode && (!user || user.role !== 'admin') 
                ? <MaintenancePage /> 
                : (user && user.user_type === 'produtor' ? <ProducerDashboard /> : <Navigate to="/login" />)
            } />
            <Route path="/termos-de-uso" element={
              siteSettings?.maintenance_mode && (!user || user.role !== 'admin') 
                ? <MaintenancePage /> 
                : <TermosDeUso />
            } />
            <Route path="/politica-de-privacidade" element={
              siteSettings?.maintenance_mode && (!user || user.role !== 'admin') 
                ? <MaintenancePage /> 
                : <PoliticaPrivacidade />
            } />
            <Route path="/sobre" element={
              siteSettings?.maintenance_mode && (!user || user.role !== 'admin') 
                ? <MaintenancePage /> 
                : <QuemSomos />
            } />
            <Route path="/cookies" element={
              siteSettings?.maintenance_mode && (!user || user.role !== 'admin') 
                ? <MaintenancePage /> 
                : <PoliticaCookies />
            } />
            <Route path="/ajuda" element={
              siteSettings?.maintenance_mode && (!user || user.role !== 'admin') 
                ? <MaintenancePage /> 
                : <CentralAjuda />
            } />
          </Routes>
          <Footer />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthContext.Provider>
  );
}

export { AuthContext };
export default App;
